import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useForm, useFieldArray, type Control } from 'react-hook-form';
import { Settings2, Plus, Trash2, Loader2, Pencil, CircleAlert, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useIntegrationCredentials,
  useIntegrationConfig,
  useUpdateIntegrationConfig,
  fetchIntegrationConfig,
  integrationConfigQueryKey,
  type CredentialSummary,
  type IntegrationConfigBody,
} from '@/hooks/use-integration-config';

const providers: Record<string, { label: string; color: string }> = {
  jira: { label: 'Jira', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  atlassian: { label: 'Atlassian', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' },
  freshservice: { label: 'Freshservice', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' },
  solarwinds: { label: 'SolarWinds', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' },
  servicenow: { label: 'ServiceNow', color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' },
  zendesk: { label: 'Zendesk', color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200' },
  freshdesk: { label: 'Freshdesk', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200' },
  okta: { label: 'Okta', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200' },
  slack: { label: 'Slack', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' },
};

const defaultColor = 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';

// Providers configurable on the dedicated ITSM Config page (deep-link target).
const ITSM_CONFIG_PROVIDERS = new Set([
  'jira',
  'jsm',
  'servicenow',
  'servicenow-serviceaccount',
  'freshservice',
]);

function getProvider(key: string) {
  // eslint-disable-next-line security/detect-object-injection
  const match = providers[key] as { label: string; color: string } | undefined;
  return match ?? { label: key.charAt(0).toUpperCase() + key.slice(1), color: defaultColor };
}

/**
 * Registry of config sections the structured (form) editor understands.
 * Section keys mirror the backend IntegrationConfigBody contract exactly.
 * Add an entry here (plus an editor) when a new config block is introduced.
 */
const CONFIG_SECTIONS = {
  tags: {
    title: 'Tags',
    description:
      'Key/value knobs consumed by well-known keys. Known: ' +
      '"outbound_record_type.<harmony_type>" routes outbound ticket creation ' +
      'to another record type (e.g. outbound_record_type.request → incident).',
  },
};

const getErrorMessage = (error: unknown): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const response = (error as { response?: { data?: { detail?: string } } }).response;
    if (response?.data?.detail) return response.data.detail;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return (error as { message: string }).message;
  }
  return 'Unknown error occurred';
};

interface FormTag {
  key: string;
  value: string;
}
interface FormValues {
  tags: FormTag[];
}

function toFormTags(tags: unknown): FormTag[] {
  if (!tags || typeof tags !== 'object' || Array.isArray(tags)) return [];
  return Object.entries(tags as Record<string, unknown>).map(([key, value]) => ({
    key,
    value: String(value),
  }));
}

function fromFormTags(rows: FormTag[]): Record<string, string> {
  return Object.fromEntries(
    rows.filter((row) => row.key.trim()).map((row) => [row.key.trim(), row.value]),
  );
}

/**
 * Known tag keys and their suggested values, offered via datalist —
 * suggestions only, free-form keys/values are always allowed.
 * Extend as backend capabilities land.
 */
const TAG_SUGGESTIONS: Record<string, string[]> = {
  'outbound_record_type.request': ['incident', 'request', 'change', 'task'],
  'outbound_record_type.incident': ['incident', 'request', 'change', 'task'],
  'outbound_record_type.change': ['incident', 'request', 'change', 'task'],
};

const TAG_VALUE_SUGGESTIONS = [
  ...new Set(Object.values(TAG_SUGGESTIONS).flat()),
];

function TagsEditor({ control }: { control: Control<FormValues> }) {
  const { fields, append, remove } = useFieldArray({ control, name: 'tags' });

  return (
    <div className="space-y-2">
      <datalist id="tag-key-suggestions">
        {Object.keys(TAG_SUGGESTIONS).map((key) => (
          <option key={key} value={key} />
        ))}
      </datalist>
      <datalist id="tag-value-suggestions">
        {TAG_VALUE_SUGGESTIONS.map((value) => (
          <option key={value} value={value} />
        ))}
      </datalist>
      {fields.map((field, index) => (
        <div key={field.id} className="flex items-center gap-2">
          <Input
            placeholder="Key (e.g. outbound_record_type.request)"
            className="h-8 font-mono text-xs"
            list="tag-key-suggestions"
            {...control.register(`tags.${index}.key`)}
          />
          <Input
            placeholder="Value (e.g. incident)"
            className="h-8 font-mono text-xs"
            list="tag-value-suggestions"
            {...control.register(`tags.${index}.value`)}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => { remove(index); }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => { append({ key: '', value: '' }); }}
      >
        <Plus className="mr-1 h-4 w-4" />
        Add tag
      </Button>
    </div>
  );
}

function ConfigDialog({
  tenantId,
  provider,
  credential,
  open,
  onOpenChange,
}: {
  tenantId: string;
  provider: string;
  credential: CredentialSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: config, isLoading } = useIntegrationConfig(
    tenantId,
    open ? credential.id : null,
  );
  const updateConfig = useUpdateIntegrationConfig(tenantId, credential.id);

  const [tab, setTab] = useState('form');
  // which known sections have been added to this config
  const [hasTags, setHasTags] = useState(false);
  // config keys the form editor doesn't know — carried through saves untouched
  const [extraKeys, setExtraKeys] = useState<Record<string, unknown>>({});
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    defaultValues: { tags: [] },
  });

  const loadConfig = (body: IntegrationConfigBody) => {
    // external_categories is edited in the ITSM Config page now; carry it
    // through untouched via extraKeys so saving tags never drops it.
    const { tags, ...rest } = body;
    form.reset({ tags: toFormTags(tags) });
    setHasTags(tags != null);
    setExtraKeys(rest);
  };

  useEffect(() => {
    if (!config) return;
    loadConfig(config.config);
    setTab('form');
    setJsonError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, form]);

  const assembleConfig = (): IntegrationConfigBody => ({
    ...extraKeys,
    tags: hasTags ? fromFormTags(form.getValues().tags) : null,
  });

  const parseJson = (): IntegrationConfigBody | null => {
    try {
      const parsed: unknown = JSON.parse(jsonText);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError('Config must be a JSON object');
        return null;
      }
      setJsonError(null);
      return parsed as IntegrationConfigBody;
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      return null;
    }
  };

  const handleTabChange = (next: string) => {
    if (next === 'json') {
      setJsonText(JSON.stringify(assembleConfig(), null, 2));
      setJsonError(null);
    } else {
      const parsed = parseJson();
      if (!parsed) return; // stay on JSON tab until it parses
      loadConfig(parsed);
    }
    setTab(next);
  };

  const handleSave = () => {
    const payload = tab === 'json' ? parseJson() : assembleConfig();
    if (!payload) return;
    updateConfig.mutate(payload, {
      onSuccess: () => {
        toast.success('Configuration saved');
        onOpenChange(false);
      },
      onError: (error: unknown) => {
        toast.error('Failed to save configuration', {
          description: getErrorMessage(error),
        });
      },
    });
  };

  const providerInfo = getProvider(provider);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[680px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${providerInfo.color}`}>
              {providerInfo.label}
            </span>
            Configuration
          </DialogTitle>
          <DialogDescription>
            Per-provider configuration, shared by all {providerInfo.label} integrations
            of this tenant.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <Tabs value={tab} onValueChange={handleTabChange}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="form">Form</TabsTrigger>
              <TabsTrigger value="json">JSON</TabsTrigger>
            </TabsList>

            <TabsContent value="form" className="space-y-4 pt-2">
              {hasTags ? (
                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium">{CONFIG_SECTIONS.tags.title}</p>
                    <p className="text-muted-foreground text-xs">
                      {CONFIG_SECTIONS.tags.description}
                    </p>
                  </div>
                  <TagsEditor control={form.control} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive h-7 text-xs"
                    onClick={() => {
                      form.setValue('tags', []);
                      setHasTags(false);
                    }}
                  >
                    <Trash2 className="mr-1 h-3 w-3" />
                    Remove section
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/60 hover:text-foreground flex w-full flex-col items-start gap-1 rounded-md border border-dashed p-4 text-left transition-colors"
                  onClick={() => { setHasTags(true); }}
                >
                  <span className="flex items-center gap-1 text-sm font-medium">
                    <Plus className="h-4 w-4" />
                    Add {CONFIG_SECTIONS.tags.title.toLowerCase()}
                  </span>
                  <span className="text-xs">{CONFIG_SECTIONS.tags.description}</span>
                </button>
              )}

              {Object.keys(extraKeys).length > 0 && (
                <p className="text-muted-foreground text-xs">
                  Other config keys (edit via JSON tab):{' '}
                  <span className="font-mono">{Object.keys(extraKeys).join(', ')}</span>
                </p>
              )}
            </TabsContent>

            <TabsContent value="json" className="space-y-2 pt-2">
              <Textarea
                value={jsonText}
                onChange={(e) => { setJsonText(e.target.value); }}
                spellCheck={false}
                className="min-h-[320px] font-mono text-xs"
              />
              {jsonError && (
                <p className="text-destructive flex items-center gap-1 text-xs">
                  <CircleAlert className="h-3.5 w-3.5" />
                  {jsonError}
                </p>
              )}
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => { onOpenChange(false); }}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} disabled={updateConfig.isPending || isLoading}>
            {updateConfig.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Keys with meaningful configuration. The connect flow now seeds an "empty"
 * config (bare provider_config + false flags + nulls) — that shape must not
 * read as configured.
 */
const configSummaryKeys = (config: IntegrationConfigBody | undefined): string[] =>
  Object.entries(config ?? {})
    .filter(([k, v]) => {
      if (v == null || v === false) return false;
      if (k === 'provider_config' && typeof v === 'object') {
        return Object.entries(v as Record<string, unknown>).some(
          ([pk, pv]) => pk !== 'provider' && pv != null,
        );
      }
      return true;
    })
    .map(([k, v]) => (Array.isArray(v) ? `${k} · ${v.length}` : k));

interface IntegrationConfigSectionProps {
  tenantId: string;
}

export function IntegrationConfigSection({ tenantId }: IntegrationConfigSectionProps) {
  const { data: credentials, isLoading: credentialsLoading } =
    useIntegrationCredentials(tenantId);
  const [editingProvider, setEditingProvider] = useState<string | null>(null);
  const navigate = useNavigate();

  // Config is stored per (tenant, provider): group credentials by provider and
  // use the first credential as the handle for that provider's config.
  // ITSM providers are managed on the dedicated ITSM Config page — they get a
  // link row here instead of the generic dialog.
  const { itsmGroups, otherGroups } = useMemo(() => {
    const groups = new Map<string, CredentialSummary[]>();
    for (const cred of credentials ?? []) {
      const group = groups.get(cred.provider) ?? [];
      group.push(cred);
      groups.set(cred.provider, group);
    }
    const all = [...groups.entries()].map(([provider, creds]) => ({ provider, creds }));
    return {
      itsmGroups: all.filter((g) => ITSM_CONFIG_PROVIDERS.has(g.provider)),
      otherGroups: all.filter((g) => !ITSM_CONFIG_PROVIDERS.has(g.provider)),
    };
  }, [credentials]);

  // ponytail: N config GETs (cached, shared with the dialog). Replace with a
  // list-configs endpoint if tenants grow far beyond ~20 integrations.
  const configQueries = useQueries({
    queries: otherGroups.map((group) => ({
      queryKey: integrationConfigQueryKey(tenantId, group.creds[0].id),
      queryFn: () => fetchIntegrationConfig(tenantId, group.creds[0].id),
      enabled: !!tenantId,
    })),
  });

  const isLoading = credentialsLoading || configQueries.some((q) => q.isLoading);

  const rows = otherGroups.map((group, i) => ({
    ...group,
    // eslint-disable-next-line security/detect-object-injection
    summary: configSummaryKeys(configQueries[i]?.data?.config),
  }));
  const configured = rows.filter((row) => row.summary.length > 0);
  const available = rows.filter((row) => row.summary.length === 0);

  const editing = otherGroups.find((g) => g.provider === editingProvider);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Integration Configuration
          </CardTitle>
          {!isLoading && available.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add configuration
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
                {available.map(({ provider }) => {
                  const info = getProvider(provider);
                  return (
                    <DropdownMenuItem
                      key={provider}
                      onSelect={() => { setEditingProvider(provider); }}
                    >
                      <span className={`mr-2 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-semibold ${info.color}`}>
                        {info.label}
                      </span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-8 w-full" />
          ) : itsmGroups.length === 0 && configured.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              No integration configuration yet
              {available.length > 0 && ' — use "Add configuration" to create one'}
            </p>
          ) : (
            <div className="grid gap-3">
              {/* ITSM providers: configured on the dedicated ITSM Config page */}
              {itsmGroups.map(({ provider, creds }) => {
                const info = getProvider(provider);
                return (
                  <div key={provider} className="flex items-center justify-between gap-2">
                    <span className={`inline-flex shrink-0 items-center rounded-md px-2.5 py-1 text-xs font-semibold ${info.color}`}>
                      {info.label}
                    </span>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground flex cursor-pointer items-center gap-1 text-xs transition-colors"
                      onClick={() => {
                        void navigate({
                          to: '/itsm-config',
                          search: { tenantId, credentialId: creds[0].id },
                        });
                      }}
                    >
                      Managed in ITSM Config
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
              {configured.map(({ provider, summary }) => {
                const info = getProvider(provider);
                return (
                  <div key={provider} className="flex items-center justify-between gap-2">
                    <span className={`inline-flex shrink-0 items-center rounded-md px-2.5 py-1 text-xs font-semibold ${info.color}`}>
                      {info.label}
                    </span>
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex flex-wrap justify-end gap-1">
                        {summary.map((k) => (
                          <span
                            key={k}
                            className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 font-mono text-xs"
                          >
                            {k}
                          </span>
                        ))}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        title="Edit configuration"
                        onClick={() => { setEditingProvider(provider); }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {editing && (
        <ConfigDialog
          tenantId={tenantId}
          provider={editing.provider}
          credential={editing.creds[0]}
          open={editingProvider !== null}
          onOpenChange={(open) => {
            if (!open) setEditingProvider(null);
          }}
        />
      )}
    </>
  );
}
